/**
 * ApprovalsPage - Approval Workflow Dashboard
 * PRD-3602: Multi-level approval workflow for campaign actions
 * 
 * Features:
 * - Pending approvals list with filtering
 * - Approve/Reject with comments
 * - Bulk approve/reject
 * - Approval history
 * - Stats overview
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  ApprovalRequest,
  getPendingApprovals,
  getApprovalHistory,
  approveAction,
  rejectAction,
  bulkApprove,
  bulkReject,
  getRiskLevelColor,
  getApprovalLevelColor,
  formatImpact,
  getTimeUntilExpiration,
} from '../lib/approvalsApi';
import { LoadingSpinner } from '../components/LoadingSpinner';

// Simple platform badge component
const PlatformBadge = ({ platform }: { platform: string }) => {
  const colors: Record<string, string> = {
    meta: 'bg-blue-100 text-blue-800',
    google: 'bg-red-100 text-red-800',
    tiktok: 'bg-black text-white',
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${colors[platform] || 'bg-gray-100 text-gray-800'}`}>
      {platform}
    </span>
  );
};

type Tab = 'pending' | 'history';
type SortField = 'requested_at' | 'estimated_impact' | 'risk_level' | 'expiration_at';
type SortDirection = 'asc' | 'desc';

// Mock data for demo
const MOCK_PENDING: ApprovalRequest[] = [
  {
    action_id: 'act_001',
    action_type: 'adjust_budget',
    platform: 'meta',
    campaign_name: 'Spring 2026 Brand Awareness',
    campaign_id: 'camp_001',
    requested_by: 'user_001',
    requester_name: 'John Smith',
    requester_email: 'john@goodpower.org',
    requested_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    status: 'pending_approval',
    approval_level: 'manager',
    estimated_impact: 5500,
    risk_level: 'medium',
    description: 'Increase daily budget from $150 to $200',
    parameters: { old_budget: 150, new_budget: 200 },
    expiration_at: new Date(Date.now() + 22 * 60 * 60 * 1000).toISOString(),
  },
  {
    action_id: 'act_002',
    action_type: 'pause_campaign',
    platform: 'google',
    campaign_name: 'Q1 Performance Max',
    campaign_id: 'camp_002',
    requested_by: 'user_002',
    requester_name: 'Sarah Johnson',
    requester_email: 'sarah@goodpower.org',
    requested_at: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    status: 'pending_approval',
    approval_level: 'director',
    estimated_impact: 12500,
    risk_level: 'high',
    description: 'Pause campaign due to underperformance (ROAS 0.8x)',
    parameters: { daily_spend: 250, reason: 'underperformance' },
    expiration_at: new Date(Date.now() + 20 * 60 * 60 * 1000).toISOString(),
  },
  {
    action_id: 'act_003',
    action_type: 'adjust_budget',
    platform: 'tiktok',
    campaign_name: 'Gen Z Engagement Drive',
    campaign_id: 'camp_003',
    requested_by: 'user_003',
    requester_name: 'Mike Chen',
    requester_email: 'mike@goodpower.org',
    requested_at: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
    status: 'pending_approval',
    approval_level: 'manager',
    estimated_impact: 800,
    risk_level: 'low',
    description: 'Increase budget by 15% for weekend push',
    parameters: { old_budget: 100, new_budget: 115 },
    expiration_at: new Date(Date.now() + 23 * 60 * 60 * 1000).toISOString(),
  },
];

const MOCK_HISTORY: ApprovalRequest[] = [
  {
    ...MOCK_PENDING[0],
    action_id: 'act_010',
    status: 'approved',
    approved_by: 'manager_001',
    approved_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    requested_at: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
  },
  {
    ...MOCK_PENDING[1],
    action_id: 'act_011',
    status: 'rejected',
    approved_by: 'director_001',
    approved_at: new Date(Date.now() - 48 * 60 * 60 * 1000).toISOString(),
    requested_at: new Date(Date.now() - 50 * 60 * 60 * 1000).toISOString(),
    rejection_reason: 'Campaign shows improvement in last 24h, monitor before pausing',
  },
];

export default function ApprovalsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('pending');
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalRequest[]>([]);
  const [historyApprovals, setHistoryApprovals] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // Sorting
  const [sortField, setSortField] = useState<SortField>('requested_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Modals
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingAction, setRejectingAction] = useState<ApprovalRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [bulkRejectModalOpen, setBulkRejectModalOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState('');
  
  // Processing states
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const orgId = user?.organization_id || 'org_default';
      
      // Try API first, fall back to mock data
      try {
        const [pending, history] = await Promise.all([
          getPendingApprovals(orgId),
          getApprovalHistory(orgId, 30),
        ]);
        setPendingApprovals(pending.length > 0 ? pending : MOCK_PENDING);
        setHistoryApprovals(history.length > 0 ? history : MOCK_HISTORY);
      } catch {
        // Use mock data for demo
        setPendingApprovals(MOCK_PENDING);
        setHistoryApprovals(MOCK_HISTORY);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sorting
  const sortApprovals = (approvals: ApprovalRequest[]) => {
    return [...approvals].sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'requested_at':
          comparison = new Date(a.requested_at).getTime() - new Date(b.requested_at).getTime();
          break;
        case 'estimated_impact':
          comparison = a.estimated_impact - b.estimated_impact;
          break;
        case 'risk_level': {
          const riskOrder = { critical: 4, high: 3, medium: 2, low: 1 };
          comparison = (riskOrder[a.risk_level] || 0) - (riskOrder[b.risk_level] || 0);
          break;
        }
        case 'expiration_at':
          comparison = new Date(a.expiration_at).getTime() - new Date(b.expiration_at).getTime();
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Selection
  const toggleSelection = (actionId: string) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(actionId)) {
      newSelection.delete(actionId);
    } else {
      newSelection.add(actionId);
    }
    setSelectedIds(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === pendingApprovals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingApprovals.map(a => a.action_id)));
    }
  };

  // Actions
  const handleApprove = async (action: ApprovalRequest) => {
    try {
      setProcessingIds(prev => new Set(prev).add(action.action_id));
      
      await approveAction(action.action_id, user?.id || 'approver_001');
      
      // Remove from pending
      setPendingApprovals(prev => prev.filter(a => a.action_id !== action.action_id));
      
      // Add to history
      setHistoryApprovals(prev => [{
        ...action,
        status: 'approved',
        approved_by: user?.id,
        approved_at: new Date().toISOString(),
      }, ...prev]);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev);
        next.delete(action.action_id);
        return next;
      });
    }
  };

  const openRejectModal = (action: ApprovalRequest) => {
    setRejectingAction(action);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!rejectingAction || !rejectReason.trim()) return;
    
    try {
      setProcessingIds(prev => new Set(prev).add(rejectingAction.action_id));
      
      await rejectAction(rejectingAction.action_id, user?.id || 'approver_001', rejectReason);
      
      // Remove from pending
      setPendingApprovals(prev => prev.filter(a => a.action_id !== rejectingAction.action_id));
      
      // Add to history
      setHistoryApprovals(prev => [{
        ...rejectingAction,
        status: 'rejected',
        approved_by: user?.id,
        approved_at: new Date().toISOString(),
        rejection_reason: rejectReason,
      }, ...prev]);
      
      setRejectModalOpen(false);
      setRejectingAction(null);
      setRejectReason('');
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      if (rejectingAction) {
        setProcessingIds(prev => {
          const next = new Set(prev);
          next.delete(rejectingAction.action_id);
          return next;
        });
      }
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      const ids = Array.from(selectedIds);
      ids.forEach(id => setProcessingIds(prev => new Set(prev).add(id)));
      
      await bulkApprove(ids, user?.id || 'approver_001');
      
      // Move approved to history
      const approved = pendingApprovals.filter(a => selectedIds.has(a.action_id));
      setPendingApprovals(prev => prev.filter(a => !selectedIds.has(a.action_id)));
      setHistoryApprovals(prev => [
        ...approved.map(a => ({
          ...a,
          status: 'approved' as const,
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
        })),
        ...prev,
      ]);
      
      setSelectedIds(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk approve');
    } finally {
      setProcessingIds(new Set());
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.size === 0 || !bulkRejectReason.trim()) return;
    
    try {
      const ids = Array.from(selectedIds);
      ids.forEach(id => setProcessingIds(prev => new Set(prev).add(id)));
      
      await bulkReject(ids, user?.id || 'approver_001', bulkRejectReason);
      
      // Move rejected to history
      const rejected = pendingApprovals.filter(a => selectedIds.has(a.action_id));
      setPendingApprovals(prev => prev.filter(a => !selectedIds.has(a.action_id)));
      setHistoryApprovals(prev => [
        ...rejected.map(a => ({
          ...a,
          status: 'rejected' as const,
          approved_by: user?.id,
          approved_at: new Date().toISOString(),
          rejection_reason: bulkRejectReason,
        })),
        ...prev,
      ]);
      
      setSelectedIds(new Set());
      setBulkRejectModalOpen(false);
      setBulkRejectReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to bulk reject');
    } finally {
      setProcessingIds(new Set());
    }
  };

  // Stats
  const stats = {
    pending: pendingApprovals.length,
    approved: historyApprovals.filter(a => a.status === 'approved').length,
    rejected: historyApprovals.filter(a => a.status === 'rejected').length,
    totalImpact: pendingApprovals.reduce((sum, a) => sum + a.estimated_impact, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Approval Queue</h1>
          <p className="text-gray-600">Review and approve campaign actions</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-600 underline mt-1">
            Dismiss
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="text-sm text-gray-500">Pending</div>
          <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="text-sm text-gray-500">Approved (30d)</div>
          <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
          <div className="text-sm text-gray-500">Rejected (30d)</div>
          <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="text-sm text-gray-500">Pending Impact</div>
          <div className="text-2xl font-bold text-blue-600">{formatImpact(stats.totalImpact)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('pending')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Pending Approvals ({pendingApprovals.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            History
          </button>
        </nav>
      </div>

      {/* Bulk Actions */}
      {activeTab === 'pending' && selectedIds.size > 0 && (
        <div className="bg-blue-50 p-4 rounded-lg flex items-center justify-between">
          <span className="text-blue-700 font-medium">
            {selectedIds.size} item{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleBulkApprove}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm"
            >
              Approve All
            </button>
            <button
              onClick={() => setBulkRejectModalOpen(true)}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm"
            >
              Reject All
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm"
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Pending Approvals List */}
      {activeTab === 'pending' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === pendingApprovals.length && pendingApprovals.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Campaign
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('estimated_impact')}
                >
                  Impact {sortField === 'estimated_impact' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('risk_level')}
                >
                  Risk {sortField === 'risk_level' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Level
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('expiration_at')}
                >
                  Expires {sortField === 'expiration_at' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Requester
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortApprovals(pendingApprovals).map((approval) => (
                <tr key={approval.action_id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(approval.action_id)}
                      onChange={() => toggleSelection(approval.action_id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <PlatformBadge platform={approval.platform} />
                      <div>
                        <div className="font-medium text-gray-900">{approval.campaign_name}</div>
                        <div className="text-sm text-gray-500">{approval.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="font-medium text-gray-900">{formatImpact(approval.estimated_impact)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRiskLevelColor(approval.risk_level)}`}>
                      {approval.risk_level}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getApprovalLevelColor(approval.approval_level)}`}>
                      {approval.approval_level}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-600">{getTimeUntilExpiration(approval.expiration_at)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">{approval.requester_name}</div>
                    <div className="text-xs text-gray-500">{approval.requester_email}</div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleApprove(approval)}
                        disabled={processingIds.has(approval.action_id)}
                        className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                      >
                        {processingIds.has(approval.action_id) ? '...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => openRejectModal(approval)}
                        disabled={processingIds.has(approval.action_id)}
                        className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {pendingApprovals.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No pending approvals
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* History */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Decision Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {historyApprovals.map((approval) => (
                <tr key={approval.action_id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <PlatformBadge platform={approval.platform} />
                      <div>
                        <div className="font-medium text-gray-900">{approval.campaign_name}</div>
                        <div className="text-sm text-gray-500">{approval.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      approval.status === 'approved' ? 'bg-green-100 text-green-800' :
                      approval.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {approval.status}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="font-medium text-gray-900">{formatImpact(approval.estimated_impact)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-600">
                      {approval.approved_at ? new Date(approval.approved_at).toLocaleDateString() : '-'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-600">{approval.rejection_reason || '-'}</span>
                  </td>
                </tr>
              ))}
              {historyApprovals.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No approval history
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Reject Modal */}
      {rejectModalOpen && rejectingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Reject Action</h3>
            <p className="text-gray-600 mb-4">
              Rejecting: <strong>{rejectingAction.description}</strong>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (required)
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={3}
              placeholder="Provide a reason for rejection..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setRejectModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reject Modal */}
      {bulkRejectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Bulk Reject</h3>
            <p className="text-gray-600 mb-4">
              Rejecting {selectedIds.size} action{selectedIds.size > 1 ? 's' : ''}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason (required for all)
            </label>
            <textarea
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={3}
              placeholder="Provide a reason for rejection..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setBulkRejectModalOpen(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkReject}
                disabled={!bulkRejectReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                Reject All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
